package debug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlin.math.abs
import kotlin.math.max
import kotlin.math.min
import kotlin.random.Random
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Canvas
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Pre
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLVideoElement
import org.w3c.dom.events.Event
import puzzlekey.Crypto
import puzzlekey.DEFAULT_CANDIDATES_PER_FRAME
import puzzlekey.PROJECTIONS
import puzzlekey.PROJECTION_SEED
import puzzlekey.PBKDF2_ITERATIONS
import puzzlekey.Projector
import puzzlekey.RecentEmbeddings
import puzzlekey.WebScanModel
import scan.SCAN_INTERVAL_MS
import scan.CameraStage
import scan.Scan
import scan.Scanner
import scan.cameraEnded
import scan.describeCameraError
import scan.loadCentre
import scan.loadScanModel

/** Shown on every page (debug view, check mode, her unlock), since the phone has no devtools: bump it on every deploy. */
const val BUILD = "T17-2026-10-14-2"

/** Scans averaged in the timing line. */
private const val TIMING_WINDOW = 10

/**
 * The debug view of the Scan (T12): the camera under the guide, every [SCAN_INTERVAL_MS] a Scan, and
 * its timings and bit confidences. Also times one candidate's PBKDF2 (ADR-0012), e.g. on her phone model.
 * It shows the live projections of whatever the camera sees, nothing from `sealed.json`.
 */
@Composable
fun ScanDebug() {
    val errors = remember { mutableStateListOf<String>() }
    var status by remember { mutableStateOf("loading the model…") }
    var scanner by remember { mutableStateOf<Scanner?>(null) }
    var video by remember { mutableStateOf<HTMLVideoElement?>(null) }
    var cameraOn by remember { mutableStateOf(false) }
    var paused by remember { mutableStateOf(false) }
    var averaging by remember { mutableStateOf(1) }
    var last by remember { mutableStateOf<Scan?>(null) }
    var flips by remember { mutableStateOf<Int?>(null) }
    val recentTotals = remember { mutableStateListOf<Double>() }
    var benchmark by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    DisposableEffect(Unit) {
        val onError: (Event) -> Unit = { e -> errors += "${e.asDynamic().message ?: e.type}" }
        val onRejection: (Event) -> Unit = { e -> errors += "${e.asDynamic().reason}" }
        window.addEventListener("error", onError)
        window.addEventListener("unhandledrejection", onRejection)
        onDispose {
            window.removeEventListener("error", onError)
            window.removeEventListener("unhandledrejection", onRejection)
        }
    }

    LaunchedEffect(Unit) {
        try {
            val t0 = window.performance.now()
            val model: WebScanModel = loadScanModel()
            val centre = loadCentre().centre
            val t1 = window.performance.now()
            val projector = Projector(centre, PROJECTION_SEED)
            val t2 = window.performance.now()
            scanner = Scanner(model, projector)
            status = "model ${ms(t1 - t0)}, projections ${ms(t2 - t1)}"
        } catch (e: Throwable) {
            status = "model failed: ${e.message ?: e}"
        }
    }

    LaunchedEffect(scanner, cameraOn, paused) {
        val s = scanner ?: return@LaunchedEffect
        val v = video ?: return@LaunchedEffect
        if (!cameraOn || paused) return@LaunchedEffect
        while (isActive) {
            val start = window.performance.now()
            if (cameraEnded(v)) {
                cameraOn = false
                errors += "the camera stopped (tab in the background?): tap to start it again"
                break
            }
            try {
                val scan = s.scan(v)
                flips = last?.let { previous -> scan.projection.indices.count { (scan.projection[it] > 0) != (previous.projection[it] > 0) } }
                last = scan
                recentTotals += scan.timings.total
                if (recentTotals.size > TIMING_WINDOW) recentTotals.removeAt(0)
            } catch (e: Throwable) {
                errors += "scan: ${e.message ?: e}"
            }
            delay(max(0.0, SCAN_INTERVAL_MS - (window.performance.now() - start)).toLong())
        }
    }

    CameraStage(
        cameraOn = cameraOn,
        onVideo = { video = it },
        onStarted = {
            scanner?.recent?.clear()
            last = null
            cameraOn = true
        },
        onError = { errors += describeCameraError(it) },
    )

    Div({ classes("panel") }) {
        Pre { Text(summary(status, last, recentTotals, flips, averaging)) }
        BitsStrip(last)
        Div({ classes("row") }) {
            for (n in 1..3) {
                Button({
                    if (n == averaging) classes("on")
                    onClick {
                        averaging = n
                        scanner?.recent = RecentEmbeddings(n)
                    }
                }) { Text(if (n == 1) "1 frame" else "avg $n") }
            }
        }
        Div({ classes("row") }) {
            Button({
                if (paused) classes("on")
                onClick { paused = !paused }
            }) { Text(if (paused) "Resume" else "Pause") }
            Button({
                onClick {
                    val wasPaused = paused
                    paused = true // the Scan loop would share the phone's time with the measurement
                    benchmark = "PBKDF2: measuring…"
                    scope.launch {
                        benchmark = try {
                            val ms = benchmarkPbkdf2()
                            "PBKDF2-SHA256 × $PBKDF2_ITERATIONS: ${ms(ms)} per candidate (median of 3)\n" +
                                "→ ${ms(ms * DEFAULT_CANDIDATES_PER_FRAME)} per frame at $DEFAULT_CANDIDATES_PER_FRAME candidates"
                        } catch (e: Throwable) {
                            "PBKDF2 failed: ${e.message ?: e}"
                        }
                        paused = wasPaused
                    }
                }
            }) { Text("Benchmark PBKDF2") }
        }
        benchmark?.let { Pre { Text(it) } }
        if (errors.isNotEmpty()) Pre({ classes("errors") }) { Text(errors.joinToString("\n") { "⚠ $it" }) }
        Div({ classes("build") }) { Text("build $BUILD") }
    }
}

private fun summary(status: String, scan: Scan?, totals: List<Double>, flips: Int?, averaging: Int): String = buildString {
    appendLine(status)
    if (scan == null) {
        append("no Scan yet")
        return@buildString
    }
    val t = scan.timings
    appendLine("last: grab ${ms(t.grab)} · input ${ms(t.input)} · model ${ms(t.model)} · project ${ms(t.project)}")
    appendLine("total ${ms(t.total)}, mean of ${totals.size}: ${ms(totals.average())} (every $SCAN_INTERVAL_MS ms)")
    val confidences = scan.projection.map { abs(it) }.sorted()
    appendLine("|p| min ${fmt(confidences.first())} · median ${fmt(confidences[confidences.size / 2])} · max ${fmt(confidences.last())}")
    appendLine("5 least confident: ${confidences.take(5).joinToString(" ") { fmt(it) }}")
    append("bits flipped since the last frame: ${flips ?: "–"} / $PROJECTIONS · averaging $averaging")
}

/**
 * The [PROJECTIONS] live bits, 32 per row: orange for 1, blue for 0, faint when the bit is unsure
 * (|projection| small against this frame's 90th percentile). All bits, since the mask comes with `sealed.json`.
 */
@Composable
private fun BitsStrip(scan: Scan?) {
    var canvas by remember { mutableStateOf<HTMLCanvasElement?>(null) }
    Canvas({
        classes("bits")
        attr("width", "${COLUMNS * CELL}")
        attr("height", "${PROJECTIONS / COLUMNS * CELL}")
        ref { element ->
            canvas = element
            onDispose { canvas = null }
        }
    })
    LaunchedEffect(scan, canvas) {
        val c = canvas ?: return@LaunchedEffect
        val ctx = c.getContext("2d") as CanvasRenderingContext2D
        ctx.clearRect(0.0, 0.0, c.width.toDouble(), c.height.toDouble())
        val p = scan?.projection ?: return@LaunchedEffect
        val scale = p.map { abs(it) }.sorted()[p.size * 9 / 10].takeIf { it > 0 } ?: 1.0
        for (i in p.indices) {
            val alpha = min(1.0, 0.15 + abs(p[i]) / scale)
            ctx.fillStyle = if (p[i] > 0) "rgba(255,140,40,$alpha)" else "rgba(70,140,255,$alpha)"
            ctx.fillRect((i % COLUMNS * CELL).toDouble(), (i / COLUMNS * CELL).toDouble(), CELL - 1.0, CELL - 1.0)
        }
    }
}

private const val COLUMNS = 32
private const val CELL = 10

/** Median milliseconds of one PBKDF2-SHA256 at [PBKDF2_ITERATIONS] over a Puzzle-bits-sized password, after a warm-up. */
private suspend fun benchmarkPbkdf2(): Double {
    val password = Random.nextBytes(9) // 71 Puzzle bits, packed
    val salt = Random.nextBytes(16)
    Crypto.pbkdf2(password, salt, 1_000, 32)
    val times = List(3) {
        val t0 = window.performance.now()
        Crypto.pbkdf2(password, salt, PBKDF2_ITERATIONS, 32)
        window.performance.now() - t0
    }
    return times.sorted()[1]
}

private fun ms(value: Double) = "${value.toInt()} ms"

private fun fmt(value: Double) = value.asDynamic().toFixed(3) as String
